/**
 * TaskDetailsModal - Modal premium de detalhes da tarefa
 * 
 * Layout moderno estilo ClickUp/Linear com:
 * - Header compacto (48px) com status, ações e título
 * - Sistema de abas: Resumo, Andamentos, Subtarefas, Anexos
 * - Sidebar fixa (360px) com informações contextuais
 * - Tamanho: 95vw x 97vh (mesmo do DealDetailsModalPremium)
 * 
 * @version 1.5.0
 * RELEASE: 2026-04-06 v1.0.0 - Criação inicial
 * RELEASE: 2026-08-21 v1.1.0 - Aba "Chat": lista conversas do contato vinculado à tarefa
 *   (reaproveita CustomerChatTab + ChatConversationModal usados nos modais de CRM)
 * RELEASE: 2026-08-21 v1.2.0 - Ação "Alterar Fase" no header (respeita tipo da
 *   tarefa e permissão granular can_change_phase)
 * RELEASE: 2026-09-03 v1.3.0 - Trilha de fases (estilo Bitrix) entre header e abas,
 *   válida para Tarefas, Retenção e Jurídico
 * RELEASE: 2026-09-12 v1.4.0 - Aba "Fluxos" com o painel de processos do Workflow
 *   (WorkflowProcessPanel), mapeando task_type para a entidade-raiz do processo
 *   (normal → task, retention → retention, legal → legal). Mesma experiência da
 *   aba "Fluxos de trabalho" do modal de Negócio
 * RELEASE: 2026-09-13 v1.5.0 - Contador da aba Fluxos passa a considerar apenas
 *   processos ativos, reaproveitando o cache da timeline da entidade correta.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTaskDetails, type ActiveSubtask } from '@/hooks/useTaskDetails';
import { useTaskModalRealtime } from '@/hooks/useTaskModalRealtime';
import { useTaskPhasePermissions } from '@/hooks/useTaskPhasePermissions';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/use-mobile';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { TaskModalHeader } from './TaskModalHeader';
import { TaskModalTabs, type TaskModalTab } from './TaskModalTabs';
import { TaskModalSidebar } from './TaskModalSidebar';
import { TaskSummaryTab } from './tabs/TaskSummaryTab';
// TaskCommentsTab removido — andamentos agora ficam na timeline do TaskSummaryTab
import { TaskFormDialog } from './TaskFormDialog';
import { TaskSubtasksTab } from './tabs/TaskSubtasksTab';
import { TaskAttachmentsTab } from './tabs/TaskAttachmentsTab';
import { TaskPausesTab } from './tabs/TaskPausesTab';
import { TaskHistoryTab } from './tabs/TaskHistoryTab';
import { TaskVehiclesTab } from './tabs/TaskVehiclesTab';
// Aba Financeiro: reaproveita 100% a estrutura de cobranças (clientes/negócios)
import { TaskChargesTab } from '@/components/financial/TaskChargesTab';
// v1.1.0 — Aba Chat reaproveitada dos modais de Cliente/Negócio
import { CustomerChatTab } from '@/components/crm/customers/modal/tabs/CustomerChatTab';
import { ChatConversationModal } from '@/components/tenant/kanban/ChatConversationModal';
import { useContactConversations } from '@/hooks/useContactConversations';
import { useCustomerVehicles } from '@/hooks/useCustomerVehicles';
// Gate da aba "Veículos": módulo do tenant + permissão granular do menu
import { useVehiclesModuleEnabled } from '@/hooks/useVehiclesModule';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import { TaskPauseReasonDialog } from './TaskPauseReasonDialog';
// Dialog de confirmação de concluir/cancelar com ajuste opcional de fase
import { TaskStatusPhaseDialog } from './TaskStatusPhaseDialog';
// v1.2.0 — Modal de alteração de categoria/fase da tarefa
import { TaskChangePhaseDialog } from './TaskChangePhaseDialog';
// v1.3.0 — Trilha de fases compartilhada (mesmo visual do modal de Negócio)
import { TaskPhaseTrail } from './TaskPhaseTrail';
// v1.4.0 — Aba "Fluxos": painel de processos do Workflow (mesmo do modal de Negócio)
import { WorkflowProcessPanel } from '@/components/workflow/WorkflowProcessPanel';
import { useWorkflowModule } from '@/hooks/useWorkflowModule';
import { useWorkflowInstanceTimeline } from '@/hooks/useWorkflowInstanceTimeline';
import { useTaskPauses } from '@/hooks/useTaskPauses';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { createLogger } from '@/lib/logger';

// Logger para o módulo
const logger = createLogger('TaskDetailsModal');

/**
 * Props do modal
 */
interface TaskDetailsModalProps {
  taskId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
  /** Aba inicial (padrão: 'summary') */
  initialTab?: TaskModalTab;
  /** Callback para abrir outra tarefa (subtarefa clicada) */
  onNavigateToTask?: (taskId: string) => void;
}

/**
 * Modal premium de detalhes da tarefa
 */
export function TaskDetailsModal({
  taskId,
  open,
  onOpenChange,
  onDeleted,
  initialTab,
  onNavigateToTask,
}: TaskDetailsModalProps) {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState<TaskModalTab>(initialTab ?? 'summary');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  /** Subtarefas ativas que bloqueiam a ação */
  const [blockingSubtasks, setBlockingSubtasks] = useState<ActiveSubtask[]>([]);
  /** Tipo da ação bloqueada */
  const [blockAction, setBlockAction] = useState<'completed' | 'cancelled' | 'delete' | null>(null);
  const [stackedTaskId, setStackedTaskId] = useState<string | null>(null);
  /** Estado do dialog de motivo de pausa */
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  /** v1.1.0: ação (concluir/cancelar) aguardando confirmação com ajuste de fase */
  const [statusPhaseAction, setStatusPhaseAction] = useState<'completed' | 'cancelled' | null>(null);
  // v1.1.0 — conversa selecionada na aba Chat (abre modal sobreposto)
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);

  // Contador de anexos exibido no badge da aba (atualiza em tempo real após upload/delete)
  const [attachmentsCount, setAttachmentsCount] = useState<number | null>(null);

  // Veículos vinculados à tarefa (badge da aba "Veículos")
  // Aba Veículos: só habilitada com módulo ativo no tenant + permissão de visualização
  const vehiclesModuleEnabled = useVehiclesModuleEnabled();
  const { canView: canViewMenu, isAdmin: isTenantAdmin, canCreate, canEdit, canDelete } = usePermissionsContext();
  const vehiclesTabEnabled =
    vehiclesModuleEnabled && (isTenantAdmin || canViewMenu('vehicles_list'));

  // Aba Financeiro: módulo financeiro do tenant + permissão de cobranças
  const { currentTenant } = useAuth();
  const chargesTabEnabled =
    (currentTenant?.financial_enabled ?? false) &&
    (isTenantAdmin || canViewMenu('financial_charges'));

  // v1.4.0 — Aba Fluxos: módulo Workflow ativo no tenant
  const { isEnabled: workflowEnabled } = useWorkflowModule();

  const { vehicles: taskVehicles } = useCustomerVehicles(
    { taskId: taskId ?? null },
    open && vehiclesTabEnabled,
  );

  // Sincroniza aba ao abrir
  useEffect(() => {
    if (open) {
      setActiveTab(initialTab ?? 'summary');
    }
  }, [open, initialTab]);

  // Invalida caches ao fechar
  useEffect(() => {
    if (!open && taskId) {
      queryClient.invalidateQueries({ queryKey: ['task-kanban-column'] });
      queryClient.invalidateQueries({ queryKey: ['task-kanban-counts'] });
      queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Realtime para timeline de andamentos (ativo apenas com modal aberto)
  useTaskModalRealtime(open ? taskId : null);

  // Hook de dados
  const {
    task,
    isLoading,
    error,
    comments,
    isLoadingComments,
    updateTask,
    isUpdating,
    changeStatus,
    isChangingStatus,
    deleteTask,
    addComment,
    isAddingComment,
    updateComment,
    isUpdatingComment,
    deleteComment,
    checkActiveSubtasks,
  } = useTaskDetails(open ? taskId : null);

  // v1.5.0 — a consulta usa a mesma chave do painel e, portanto, reaproveita o
  // cache do TanStack Query. Retenção e Jurídico permanecem entidades separadas.
  const workflowRootEntityType = task?.task_type === 'normal' ? 'task' : (task?.task_type ?? 'task');
  const { processos: workflowProcessos } = useWorkflowInstanceTimeline({
    rootEntityType: workflowRootEntityType,
    rootEntityId: task?.id ?? '',
    enabled: Boolean(open && workflowEnabled && task?.id),
  });
  const workflowActiveCount = useMemo(
    () => workflowProcessos.filter((processo) => processo.encerrada !== true).length,
    [workflowProcessos],
  );

  // v1.1.0 — conversas do contato vinculado (badge da aba Chat)
  const chatEnabled = !!task?.contact_id;
  const { data: taskConversations } = useContactConversations(task?.contact_id ?? null, {
    enabled: open && chatEnabled,
  });

  // Hook de pausas (ativo apenas quando modal aberto)
  const { pauseTask, isPausing, resumeTask, isResuming } = useTaskPauses(open ? taskId : null, false);

  // Hook de permissões granulares por fase
  const { canDoOnPhase } = useTaskPhasePermissions(task?.category_id);
  const { user: currentUser } = useAuth();

  // Verifica se o usuário logado é o responsável pela tarefa
  const isResponsible = useMemo(
    () => currentUser?.id === task?.assigned_to,
    [currentUser?.id, task?.assigned_to]
  );

  // Permissões calculadas para a fase da tarefa atual (com contexto de responsável)
  const phasePerms = {
    canEdit: canDoOnPhase(task?.phase_id, 'can_edit', isResponsible),
    canDelete: canDoOnPhase(task?.phase_id, 'can_delete', isResponsible),
    canChangeStatus: canDoOnPhase(task?.phase_id, 'can_change_status', isResponsible),
    canReopen: canDoOnPhase(task?.phase_id, 'can_reopen', isResponsible),
    canComment: canDoOnPhase(task?.phase_id, 'can_comment', isResponsible),
    canChangeResponsible: canDoOnPhase(task?.phase_id, 'can_change_responsible', isResponsible),
    // v1.2.0 — permissão de mover a tarefa de fase
    canChangePhase: canDoOnPhase(task?.phase_id, 'can_change_phase', isResponsible),
  };

  /** v1.2.0 — controle do modal "Alterar Fase" */
  const [changePhaseOpen, setChangePhaseOpen] = useState(false);

  /** Tarefa fechada (concluída ou cancelada) — bloqueia edições */
  const isClosed = task?.status === 'completed' || task?.status === 'cancelled';

  /**
   * Handler para mudar status — intercepta concluir/cancelar para verificar subtarefas
   */
  const handleChangeStatus = async (newStatus: string) => {
    // Verificar bloqueio por subtarefas ao concluir ou cancelar
    if ((newStatus === 'completed' || newStatus === 'cancelled') && task && task.subtasks_count > 0) {
      const action = newStatus === 'completed' ? 'complete' : 'cancel';
      const active = await checkActiveSubtasks(action);
      if (active.length > 0) {
        setBlockingSubtasks(active);
        setBlockAction(newStatus as 'completed' | 'cancelled');
        return;
      }
    }

    // v1.1.0: concluir/cancelar abre dialog para ajuste opcional de fase
    if (newStatus === 'completed' || newStatus === 'cancelled') {
      setStatusPhaseAction(newStatus as 'completed' | 'cancelled');
      return;
    }

    await changeStatus(newStatus);
  };

  /**
   * Handler para solicitar exclusão — intercepta para verificar subtarefas
   */
  const handleRequestDelete = async () => {
    if (task && task.subtasks_count > 0) {
      const active = await checkActiveSubtasks('delete');
      if (active.length > 0) {
        setBlockingSubtasks(active);
        setBlockAction('delete');
        return;
      }
    }
    setDeleteOpen(true);
  };


  /**
   * Handler para navegar até uma subtarefa clicada no modal de bloqueio
   * Fecha o modal de bloqueio e o modal pai, depois abre a subtarefa
   */
  const handleSubtaskNavigate = useCallback((subtaskId: string) => {
    // Fecha modal de bloqueio e abre subtarefa como modal empilhado
    setBlockingSubtasks([]);
    setBlockAction(null);
    setStackedTaskId(subtaskId);
  }, []);


  /**
   * Handler para deletar tarefa (confirmado)
   */
  const handleDelete = async () => {
    await deleteTask();
    setDeleteOpen(false);
    onOpenChange(false);
    onDeleted?.();
  };

  /**
   * Renderiza conteúdo da aba ativa
   */
  const renderTabContent = () => {
    if (!task) return null;

    switch (activeTab) {
      case 'summary':
        return (
          <TaskSummaryTab
            task={task}
            canEdit={phasePerms.canEdit}
            comments={comments}
            isLoadingComments={isLoadingComments}
            onAddComment={!isClosed && phasePerms.canComment ? addComment : undefined}
            isAddingComment={isAddingComment}
            onEditComment={!isClosed && phasePerms.canComment ? updateComment : undefined}
            isEditingComment={isUpdatingComment}
            onDeleteComment={!isClosed && phasePerms.canComment ? deleteComment : undefined}
            canComment={!isClosed && phasePerms.canComment}
            sidebarContent={isMobile ? (
              <TaskModalSidebar
                task={task}
                onUpdateTask={!isClosed && phasePerms.canChangeResponsible ? updateTask : undefined}
                isUpdating={isUpdating}
                className="w-full border-l-0"
              />
            ) : undefined}
          />
        );
      case 'subtasks':
        return (
          <TaskSubtasksTab
            task={task}
            onNavigateToTask={(id) => setStackedTaskId(id)}
            /* Veículos da tarefa pai — herdados ao criar subtarefa */
            parentVehicles={taskVehicles.map((v) => ({
              id: v.id,
              label:
                [v.plate, [v.brand_name, v.model_name].filter(Boolean).join(' ')]
                  .filter(Boolean)
                  .join(' — ') || 'Veículo vinculado',
            }))}
          />
        );
      case 'pauses':
        return <TaskPausesTab task={task} />;
      case 'attachments':
        return (
          <TaskAttachmentsTab
            taskId={task.id}
            isActive={activeTab === 'attachments'}
            canDelete={!isClosed && phasePerms.canEdit}
            onCountChange={setAttachmentsCount}
            onNavigateToComments={() => setActiveTab('summary')}
          />
        );
      case 'vehicles':
        // v1.x — múltiplos veículos por tarefa (task_vehicles)
        // Guard extra: sem módulo/permissão a aba não é renderizada
        if (!vehiclesTabEnabled) return null;
        return (
          <TaskVehiclesTab
            taskId={task.id}
            isActive={activeTab === 'vehicles'}
            readOnly={isClosed || !phasePerms.canEdit}
          />
        );
      case 'chat':
        // v1.1.0 — conversas do contato vinculado; permite iniciar nova conversa
        if (!chatEnabled) return null;
        return (
          <CustomerChatTab
            contactId={task.contact_id}
            contactName={task.contact_name ?? undefined}
            onOpenConversation={(conversationId) => {
              setSelectedConversationId(conversationId);
              setIsChatModalOpen(true);
            }}
            isActive={activeTab === 'chat'}
          />
        );
      case 'charges':
        // Cobranças vinculadas à tarefa (filtro task_id na RPC paginada)
        if (!chargesTabEnabled) return null;
        return (
          <TaskChargesTab
            taskId={task.id}
            isActive={activeTab === 'charges'}
            customerId={task.customer_id}
            dealId={task.deal_id}
            canCreate={!isClosed && (isTenantAdmin || canCreate('financial_charges'))}
            canEdit={isTenantAdmin || canEdit('financial_charges')}
            canDelete={isTenantAdmin || canDelete('financial_charges')}
          />
        );
      case 'workflow':
        // v1.4.0: processos do Workflow vinculados à tarefa. A entidade-raiz do
        // processo é derivada do módulo da tarefa (normal → 'task').
        if (!workflowEnabled) return null;
        return (
          <WorkflowProcessPanel
            rootEntityType={task.task_type === 'normal' ? 'task' : task.task_type}
            rootEntityId={task.id}
            groupByStatus
          />
        );
      case 'history':
        return <TaskHistoryTab taskId={task.id} />;
      default:
        return null;
    }
  };

  /**
   * Renderiza conteúdo interno
   */
  const renderContent = () => {
    // Loading
    if (isLoading) {
      return (
        <div className="flex flex-col h-full">
          <div className="flex items-center gap-3 px-4 h-12 border-b">
            <Skeleton className="h-5 w-16" />
            <Skeleton className="h-5 w-48 flex-1" />
            <div className="flex gap-1">
              {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-8 w-8 rounded" />)}
            </div>
          </div>
          <div className="flex items-center gap-2 h-9 px-4 border-b">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-5 w-20" />)}
          </div>
          <div className="flex flex-1 min-h-0">
            <div className="flex-1 p-4 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 rounded-lg" />)}
              </div>
              <Skeleton className="h-48 rounded-lg" />
            </div>
            <div className="w-[360px] border-l p-3 space-y-4">
              <Skeleton className="h-16 rounded-lg" />
              <Skeleton className="h-12" />
              <Skeleton className="h-32" />
            </div>
          </div>
        </div>
      );
    }

    // Erro
    if (error || !task) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4">
          <p className="text-muted-foreground">Tarefa não encontrada.</p>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </div>
      );
    }

    // Conteúdo principal
    return (
      <TooltipProvider>
        <div className="flex flex-col h-full">
          {/* Header compacto */}
          <TaskModalHeader
            task={task}
            onEdit={() => setEditMode(true)}
            onChangeStatus={handleChangeStatus}
            onDelete={() => handleRequestDelete()}
            onClose={() => onOpenChange(false)}
            onPause={() => setPauseDialogOpen(true)}
            onResume={() => resumeTask()}
            phasePermissions={phasePerms}
            onChangePhase={() => setChangePhaseOpen(true)}
          />

          {/* v1.3.0 — Trilha de fases (estilo Bitrix), igual ao modal de Negócio.
              Vale para Tarefas, Retenção e Jurídico, respeitando a estrutura
              granular de permissões por fase de cada módulo. */}
          <TaskPhaseTrail
            taskId={task.id}
            taskType={task.task_type}
            categoryId={task.category_id}
            currentPhaseId={task.phase_id}
            isClosed={isClosed}
            isResponsible={isResponsible}
            onSelectFinalPhase={() => handleChangeStatus('completed')}
          />


          {/* Sistema de abas */}
          <TaskModalTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            subtasksCount={task.subtasks_count}
            pausesCount={task.pauses_count}
            attachmentsCount={attachmentsCount ?? task.attachments_count}
            vehiclesCount={taskVehicles.length}
            vehiclesEnabled={vehiclesTabEnabled}
            chatCount={taskConversations?.length}
            chatEnabled={chatEnabled}
            chargesCount={task.charges_count}
            chargesEnabled={chargesTabEnabled}
            workflowEnabled={workflowEnabled}
            workflowCount={workflowActiveCount}
          />

          {/* Área de conteúdo + sidebar */}
          <div className={cn("flex flex-1 min-h-0", isMobile ? "flex-col" : "flex-row")}>
            {/* Conteúdo principal */}
            <div className="flex-1 min-w-0 min-h-0 overflow-y-auto scrollbar-thin p-2 md:p-4">
              {renderTabContent()}
            </div>

            {/* Sidebar fixa — oculta no mobile (vira aba "Detalhes") */}
            {!isMobile && (
              <TaskModalSidebar task={task} onUpdateTask={!isClosed && phasePerms.canChangeResponsible ? updateTask : undefined} isUpdating={isUpdating} />
            )}
          </div>

          {/* v1.1.0 — Modal de conversa sobreposto ao modal da tarefa */}
          <ChatConversationModal
            conversationId={selectedConversationId}
            open={isChatModalOpen}
            onOpenChange={(isOpen) => {
              setIsChatModalOpen(isOpen);
              if (!isOpen) setSelectedConversationId(null);
            }}
          />

          {/* Dialog de confirmação de exclusão */}
          <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Excluir Tarefa</AlertDialogTitle>
                <AlertDialogDescription>
                  Tem certeza que deseja excluir "#{task.number} - {task.title}"? Esta ação não pode ser desfeita.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Excluir
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Modal de bloqueio por subtarefas ativas */}
          <AlertDialog open={blockingSubtasks.length > 0} onOpenChange={(open) => { if (!open) { setBlockingSubtasks([]); setBlockAction(null); } }}>
            <AlertDialogContent className="sm:max-w-[520px]">
              <AlertDialogHeader>
                {/* Título com ícone inline */}
                <AlertDialogTitle className="flex items-center gap-2 text-base font-semibold">
                  <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
                  Não é possível {blockAction === 'completed' ? 'concluir' : blockAction === 'cancelled' ? 'cancelar' : 'excluir'} esta tarefa
                </AlertDialogTitle>

                {/* Descrição simples */}
                <AlertDialogDescription className="text-sm text-muted-foreground">
                  Existem subtarefas ativas que precisam ser finalizadas primeiro:
                </AlertDialogDescription>
              </AlertDialogHeader>

              {/* Lista de subtarefas bloqueantes */}
              <div className="mt-1 -mx-1">
                {blockingSubtasks.map((st, idx) => (
                  <button
                    key={st.id}
                    onClick={() => handleSubtaskNavigate(st.id)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/50 transition-colors rounded-md group',
                      idx < blockingSubtasks.length - 1 && 'border-b border-border/40'
                    )}
                  >
                    <span className="font-mono text-xs text-muted-foreground">#{st.number}</span>
                    <span className="flex-1 text-sm truncate text-foreground">{st.title}</span>
                    <span className={cn(
                      'text-[11px] px-1.5 py-0.5 rounded font-medium shrink-0',
                      st.status === 'in_progress'
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                    )}>
                      {st.status === 'in_progress' ? 'Andamento' : 'Pausada'}
                    </span>
                    <ExternalLink className="w-3.5 h-3.5 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors shrink-0" />
                  </button>
                ))}
              </div>

              {/* Footer alinhado à direita */}
              <AlertDialogFooter className="sm:justify-end">
                <AlertDialogAction
                  onClick={() => { setBlockingSubtasks([]); setBlockAction(null); }}
                >
                  Entendi
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Dialog de edição da tarefa */}
          <TaskFormDialog
            open={editMode}
            onOpenChange={setEditMode}
            editTaskId={task.id}
            onSuccess={() => {
              setEditMode(false);
              queryClient.invalidateQueries({ queryKey: ['task-details', taskId] });
            }}
          />

          {/* Dialog de motivo da pausa */}
          <TaskPauseReasonDialog
            open={pauseDialogOpen}
            onOpenChange={setPauseDialogOpen}
            onConfirm={async (reason) => {
              await pauseTask({ reason });
            }}
            isLoading={isPausing}
          />

          {/* v1.1.0: Confirmação de concluir/cancelar com ajuste opcional de fase */}
          <TaskStatusPhaseDialog
            open={statusPhaseAction !== null}
            onOpenChange={(v) => { if (!v) setStatusPhaseAction(null); }}
            action={statusPhaseAction}
            taskTitle={task.title}
            categoryId={task.category_id}
            currentPhaseId={task.phase_id}
            taskType={task.task_type}
            isSubmitting={isChangingStatus}
            onConfirm={async ({ phaseId, completionReasonId, completionNote }) => {
              await changeStatus({
                status: statusPhaseAction!,
                phaseId,
                completionReasonId,
                completionNote,
              });
              setStatusPhaseAction(null);
            }}
          />

          {/* v1.2.0: Alteração de categoria/fase da tarefa */}
          <TaskChangePhaseDialog
            open={changePhaseOpen}
            onOpenChange={setChangePhaseOpen}
            taskId={task.id}
            taskType={task.task_type}
            currentCategoryId={task.category_id}
            currentPhaseId={task.phase_id}
            currentPhaseName={task.phase_name}
            currentPhaseColor={task.phase_color}
            onAddNote={(content, contentHtml) => addComment({ content, contentHtml })}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['task-details', taskId] });
              queryClient.invalidateQueries({ queryKey: ['task-kanban-column'] });
              queryClient.invalidateQueries({ queryKey: ['task-kanban-counts'] });
              queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
              queryClient.invalidateQueries({ queryKey: ['task-activity-log', taskId] });
            }}
          />

        </div>
      </TooltipProvider>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className={cn(
            "p-0 flex flex-col overflow-hidden",
            isMobile
              ? "w-screen h-[100dvh] max-w-none max-h-none rounded-none border-0"
              : "max-w-[95vw] w-[95vw] h-[97vh]"
          )}
          preventCloseOnOutsideInteraction
          hideCloseButton
        >
          {/* Título acessível oculto */}
          <VisuallyHidden.Root>
            <DialogTitle>Detalhes da Tarefa</DialogTitle>
          </VisuallyHidden.Root>

          {renderContent()}
        </DialogContent>
      </Dialog>

      {/* Modal empilhado para subtarefa clicada no bloqueio */}
      {stackedTaskId && (
        <TaskDetailsModal
          taskId={stackedTaskId}
          open={!!stackedTaskId}
          onOpenChange={(isOpen) => { if (!isOpen) setStackedTaskId(null); }}
          onNavigateToTask={(id) => setStackedTaskId(id)}
        />
      )}
    </>
  );
}
